/**
 * Make the run's KNOWN metadata names clickable wherever they appear in an answer.
 *
 * ── WHAT WAS LOST, AND WHAT IT ACTUALLY WAS ─────────────────────────────────────────────────────
 *
 * Reported as "the file in the table was clickable and opened in the code editor". It was more than
 * tables: the pre-migration answer column linkified class names in paragraphs, list items AND table
 * cells, through `linkifyEntities` and the markdown renderer's `components` hook. That wiring lived
 * in `ResultColumn`, which the runtime migration deleted, and nothing carried it across — so an
 * answer naming `OpportunityApprovalService` became plain text and the reader had to go and find it
 * in the explorer themselves.
 *
 * ── WHY IT CANNOT BE "LINK EVERY CAPITALISED WORD" ──────────────────────────────────────────────
 *
 * The rule that makes this safe is that ONLY names the run actually touched are linked — the
 * dependency graph's targets, callers and tests, and the impact analysis's classes. Anything else
 * would underline ordinary prose nouns and offer to open files that do not exist, and a link that
 * dead-ends is worse than plain text (§1: no fabricated affordances).
 *
 * `linkifyEntities` itself is unchanged and is a plain string scan — no regex, no false positives
 * on substrings, because it only matches whole identifier runs.
 *
 * ── WHERE THE DATA COMES FROM ───────────────────────────────────────────────────────────────────
 *
 * `multiAgentIdeStore`, which the consolidation KEEPS: `analysisHydration` fills it from
 * `GET /runs/{id}/detail`'s `analysis` block, and the editor's post-save analysis tops it up.
 * Neither path is the legacy SSE, so nothing here reintroduces the old runtime.
 */
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { linkifyEntities } from "@/components/molecules/multi-agent/ProseEntityLinker";
import { buildChipIndex, chipActionOf, chipIndexSignature, type ChipIndex } from "@/services/multi-agent/laneV2/entityChips";
import type { TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { useAuthStoreV1 } from "@/stores/authStore_v1";

/** Below this a token is too generic to link — `Id`, `Map`, `Set` are not the run's classes. */
const MIN_ENTITY_LENGTH = 3;

/** What react-markdown hands a component override: the children, any class, and the hast `node`. */
interface OverrideProps {
  children?: React.ReactNode;
  className?: string;
  /** The hast node. Never spread onto a DOM element — React rejects it as an unknown prop. */
  node?: unknown;
  [attr: string]: unknown;
}

export interface EntityLinkComponents {
  p: (props: OverrideProps) => JSX.Element;
  li: (props: OverrideProps) => JSX.Element;
  td: (props: OverrideProps) => JSX.Element;
}

function joinClasses(...parts: (string | undefined)[]): string | undefined {
  const out = parts.filter((part): part is string => !!part && part.trim().length > 0).join(" ");
  return out || undefined;
}

/**
 * The three overrides, built from a linkify function. Pure and exported so the markup they produce
 * can be asserted without the store.
 *
 * ── THEY MUST KEEP STREAMDOWN'S OWN ATTRIBUTES ─────────────────────────────────────────────────
 *
 * A `components` entry REPLACES the renderer for that tag; it does not wrap it. The first version
 * of this returned a bare `<td>`, so every table body cell in an answer that had linkable names
 * lost `data-streamdown="table-cell"` — and with it every cell rule in `multi-agent-tokens.css`
 * (padding, divider, wrapping), while the `th` row, which is not overridden, kept its. Reported
 * from the real app as body cells flush against the card edge under a properly padded header. The
 * QA harness never showed it because its fixtures carry no entities, so the overrides were `null`
 * there and streamdown's own `td` rendered.
 *
 * So each override re-emits exactly what streamdown's renderer emits (`data-streamdown` marker and
 * class list, read from streamdown's `MarkdownTd` / `MarkdownLi` / `MarkdownParagraph`), then
 * linkifies the children. `node` is dropped rather than spread — it is a hast object, not a DOM
 * attribute.
 */
export function entityLinkComponents(
  link: (children: React.ReactNode) => React.ReactNode,
): EntityLinkComponents {
  return {
    p: ({ children, node: _node, ...rest }) => <p {...rest}>{link(children)}</p>,
    li: ({ children, node: _node, className, ...rest }) => (
      <li data-streamdown="list-item" className={joinClasses("py-1 [&>p]:inline", className)} {...rest}>
        {link(children)}
      </li>
    ),
    td: ({ children, node: _node, className, ...rest }) => (
      <td data-streamdown="table-cell" className={joinClasses("px-4 py-2 text-sm", className)} {...rest}>
        {link(children)}
      </td>
    ),
  };
}

/**
 * Markdown `components` overrides that linkify known names in prose, list items and table cells.
 *
 * Returns `null` when the run touched nothing — so an answer with no analysis behind it renders
 * through streamdown's own renderers, exactly as before, with no wrapper components in the tree.
 */
/**
 * On Lane V2 the same chips are fed from the session's own snapshots (UX map C16): `snapshots` = R3 by task,
 * `onOpenItem` = open that component's diff in the panel (a name in this task's package); any other name the session touched
 * links to the metadata explorer. Omitted (the classic lane), only the IDE store's names link, as before.
 */
export interface LaneV2EntityLinkSource {
  snapshots: Readonly<Record<string, TaskSnapshot | null | undefined>>;
  onOpenItem?: (taskId: string, workItemId: string) => void;
}

export function useEntityLinks(laneV2?: LaneV2EntityLinkSource): EntityLinkComponents | null {
  const snapshots = laneV2?.snapshots;
  const onOpenItem = laneV2?.onOpenItem;
  const builtChips = useMemo(() => (snapshots ? buildChipIndex(snapshots) : null), [snapshots]);
  // A snapshot refetch that names the same components must not rebuild the markdown overrides (every past turn would re-parse).
  const chipsSig = builtChips ? chipIndexSignature(builtChips) : "";
  const chips: ChipIndex | null = useMemo(() => builtChips, [chipsSig]); // eslint-disable-line react-hooks/exhaustive-deps
  const dependencies = useMultiAgentIdeStore((s) => s.dependencies);
  const impactAnalysis = useMultiAgentIdeStore((s) => s.impactAnalysis);
  const setWorkspaceCodeRequest = useMultiAgentIdeStore((s) => s.setWorkspaceCodeRequest);
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id);

  const known = useMemo(() => {
    const map = new Map<string, string | null>();
    // The session's own names first: a package member (with its diff) outranks the IDE graph's guess.
    for (const [token, t] of chips?.byToken ?? []) if (chipActionOf(t)) map.set(token, token);
    const add = (name?: string | null, id?: string | null) => {
      const n = (name || "").trim();
      if (n.length < MIN_ENTITY_LENGTH) return;
      // First writer wins for the NAME, but an id always beats a null — the same class can arrive
      // from impact analysis (no id) before the dependency graph names it (id), and the link is
      // only cheap to open when it carries one.
      if (!map.has(n)) map.set(n, id ?? null);
      else if (id && !map.get(n)) map.set(n, id);
    };
    for (const d of dependencies) {
      add(d.target, d.target_id);
      for (const c of d.callers) add(c.name, c.id);
      for (const t of d.tests) add(t.name, t.id);
    }
    for (const e of impactAnalysis) {
      add(e.class_name);
      for (const c of e.callers || []) add(c.name);
      for (const c of e.callees || []) add(c.name);
    }
    return map;
  }, [dependencies, impactAnalysis, chips]);

  const openByName = useCallback(
    async (name: string, id: string | null) => {
      const chip = chips?.byToken.get(name);
      const action = chip ? chipActionOf(chip) : null;
      if (action) {
        if (action.kind === "diff" && onOpenItem) onOpenItem(action.taskId, action.workItemId);
        else if (action.kind === "explorer") window.open(action.href, "_blank", "noopener,noreferrer");
        return;
      }
      try {
        /*
         * Imported lazily because the resolver pulls the metadata service in with it, and the
         * overwhelming majority of answers link nothing — paying for that module on every
         * transcript render would be a cost for a feature most runs do not use.
         */
        const { getMetadataItem, getMetadataItemByName } = await import(
          "@/pages/multi-agent/ide/panels/resolveDependencyItemForEditor"
        );
        const resolvedId = id || (await getMetadataItemByName(name, orgId, true)).id;
        const full = await getMetadataItem(resolvedId, orgId, true);
        setWorkspaceCodeRequest({
          id: full.id,
          name: full.name || full.full_name || name,
          metadataType: full.metadata_type || "ApexClass",
          content: full.content || "",
          badge: "R",
        });
      } catch (e) {
        // Named, because "couldn't open" without the subject leaves the reader guessing which of
        // the four links they clicked failed.
        toast.error(`Couldn't open ${name}: ${(e as Error)?.message ?? "not found"}`);
      }
    },
    [orgId, setWorkspaceCodeRequest, chips, onOpenItem],
  );

  return useMemo(() => {
    if (known.size === 0) return null;
    // A chip that is not a package member with a diff is an explorer link (a real anchor: new tab, middle-click).
    const hrefOf = chips
      ? (token: string) => {
          const chip = chips.byToken.get(token);
          const action = chip ? chipActionOf(chip) : null;
          return action?.kind === "explorer" ? action.href : null;
        }
      : undefined;
    const link = (children: React.ReactNode) => linkifyEntities(children, known, openByName, hrefOf);
    /*
     * `p`, `li` and `td` — the three places prose actually lands. NOT `code` or `pre`: a class name
     * inside a fence is source, and turning part of a code block into a button breaks selection and
     * the copy button's text. NOT `th` either: a header is a column name, not an instance.
     */
    return entityLinkComponents(link);
  }, [known, openByName, chips]);
}
